<template>
  <!-- setting `--progress` manually for proper animation on page load -->
  <!-- prettier-ignore-attribute style -->
  <div class="pricing-tariffs-slider-outer" ref="outer" style="--progress:0;">
    <!-- prettier-ignore-attribute style -->
    <div class="pricing-tariffs-slider-inner" ref="inner" style="--translate:0px;">
      <div class="pricing-tariffs-slider" ref="slider">
        <div v-if="hasSmall" class="pricing-tariffs-card-wrapper">
          <PricingTariffsCard :currency="currency" :lang="lang" :tariff="'small'"></PricingTariffsCard>
        </div>
        <div class="swiper" ref="swiper">
          <div class="swiper-wrapper">
            <template v-for="(tariff, i) in tariffs">
              <div
                v-if="tariff !== 'small'"
                class="swiper-slide"
                :data-index="i"
                ref="swiperSlide"
                @click="handleClick"
              >
                <div class="pricing-tariffs-card-wrapper">
                  <PricingTariffsCard
                    :key="`card-${i}`"
                    :currency="currency"
                    :lang="lang"
                    :tariff="tariff"
                  ></PricingTariffsCard>
                  <pricing-tariffs-features-slider
                    :key="`slider-${i}`"
                    :enabled="false"
                    :index="i"
                    :tariff="tariff"
                    :activeIndex="swiperData.activeIndex"
                    :resetSlider="resetFeaturesSlider"
                  ></pricing-tariffs-features-slider>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import eases from 'eases';
import scrollTo from '~scripts/core/scrollTo';
import translations from './pricing-translations';
import PricingTariffsCard from './PricingTariffsCard.vue';
import PricingTariffsFeaturesSlider from '../PricingTariffsFeatures/PricingTariffsFeaturesSlider.vue';

const animationData = { start: 0, speed: 200 };
// TODO: Get rid of duplication of this data in CSS and JS: pass them to CSS as custom properties
const swiperSizesStatic = { slideWidth: { lg: 700, xl: 800 }, spaceBetween: { lg: 10, xl: 20 } };

export default {
  name: 'PricingTariffsSlider',
  components: { PricingTariffsCard, PricingTariffsFeaturesSlider },
  props: {
    currency: {
      type: String,
      required: true,
    },
    lang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      animation: undefined,
      animationEnd: 0,
      swiper: undefined,
      swiperData: {
        activeIndex: 0,
        translate: 0,
      },
      swiperSizes: {
        slideWidth: swiperSizesStatic.slideWidth.lg,
        spaceBetween: swiperSizesStatic.spaceBetween.lg,
      },
      progress: 0,
      resetFeaturesSlider: false,
    };
  },
  computed: {
    breakpointXlMin() {
      return '1441px';
    },
    mediaXl() {
      return window.matchMedia(`(min-width: ${this.breakpointXlMin})`);
    },
    mediaXlMinNumber() {
      return parseInt(this.breakpointXlMin);
    },
    translations() {
      return translations[this.lang];
    },
    tariffs() {
      return Object.keys(this.translations.tariffs);
    },
    hasSmall() {
      const { currency, lang } = this;
      return lang === 'ru' && (currency === 'rub' || currency === 'kzt' || currency === 'uzs');
    },
    pricingTariffsWrapperEl() {
      return document.getElementById('pricing-tariffs-wrapper');
    },
    pricingTariffsEl() {
      return document.getElementById('pricing-tariffs');
    },
  },
  mounted() {
    this.handleMediaXlMatchesChange();
    this.mediaXl.addEventListener('change', this.handleMediaXlMatchesChange, { passive: true });

    window.addEventListener('resize', this.handleResize, { passive: true });

    this.setAnimationEnd();
    this.animationStart();

    this.setHeight();
    this.setPull();
    this.setProgress(0);
    this.setInitialSlide(1); // Opening "Maximum" by default

    // Called from `static/promo-static/landing-2021/src/scripts/pages/price/index/index.js`
    this.$root.$on('scrollToTop', () => {
      const { progress } = this;

      if (progress > 0 && progress < 1) {
        scrollTo(0, { duration: animationData.speed, easing: eases.linear });
      }
    });
  },
  beforeDestroy() {
    this.mediaXl.removeEventListener('change', this.handleMediaXlMatchesChange);

    window.removeEventListener('resize', this.handleResize);

    this.animationStop();

    this.setHeight(false);
    this.setPull(false);
    this.setProgress(0, false);
  },
  methods: {
    handleMediaXlMatchesChange() {
      const { swiperSizes } = this;

      if (this.mediaXl.matches) {
        swiperSizes.slideWidth = swiperSizesStatic.slideWidth.xl;
        swiperSizes.spaceBetween = swiperSizesStatic.spaceBetween.xl;
      } else {
        swiperSizes.slideWidth = swiperSizesStatic.slideWidth.lg;
        swiperSizes.spaceBetween = swiperSizesStatic.spaceBetween.lg;
      }
    },
    swiperCreate() {
      if (!this.swiper) {
        this.swiper = new Swiper(this.$refs.swiper, {
          allowTouchMove: true,
          breakpoints: {
            // when window width is >= this.mediaXlMinNumber
            [this.mediaXlMinNumber]: {
              spaceBetween: swiperSizesStatic.spaceBetween.xl,
            },
          },
          effect: 'slide',
          init: false,
          loop: false,
          roundLengths: true,
          slidesPerView: 'auto',
          // Works incorrect with Apple touchpad force touch,
          // using swiper.slideTo(index) method on click instead
          // slideToClickedSlide: true,
          spaceBetween: swiperSizesStatic.spaceBetween.lg,
          speed: animationData.speed,
        });

        // To restore previously active slide
        this.swiper.on('slideChangeTransitionEnd', (swiper) => {
          this.swiperData.activeIndex = swiper.activeIndex;
          this.swiperData.translate = swiper.translate;

          this.$refs.inner.style.setProperty('--translate', `${swiper.translate}px`);
        });

        this.swiper.on('touchMove', (swiper) => {
          // Using `classList.add` instead of `swiper.setGrabCursor()`,
          // because `.setGrabCursor()` sets `cursor: grab;` on `.swiper-wrapper` inline styling,
          // and we already have `cursor: pointer;` on inner element `.swiper-slide`.
          swiper.el.classList.add('grab');
        });

        this.swiper.on('touchEnd', (swiper) => {
          // swiper.unsetGrabCursor();
          swiper.el.classList.remove('grab');
        });
      }
    },
    swiperDestroy() {
      if (this.swiper && this.swiper.initialized) {
        this.swiper.destroy();
        this.swiper = undefined;
      }
    },
    animationStart() {
      const animationEnd = this.animationEnd;

      this.animation = scrollAnimationProgress(this.$refs.outer, {
        getStart() {
          return animationData.start;
        },
        getEnd(el, start) {
          return start + animationEnd;
        },
        lerpValue: 0.6,
        onProgress: (progress) => {
          const { swiper } = this;
          const slideActive = this.$refs.swiperSlide[this.swiperData.activeIndex];

          this.progress = progress;

          if (!swiper) {
            this.swiperCreate();
          }

          if (!slideActive.classList.contains('swiper-slide-active')) {
            slideActive.classList.add('swiper-slide-active');
          }

          if (progress === 0) {
            // Reset features sliders to the first slide
            this.resetFeaturesSlider = !this.resetFeaturesSlider;
          } else if (progress > 0 && progress < 1) {
            if (swiper && swiper.initialized) {
              this.swiperDestroy();
            }
          } else if (progress === 1) {
            if (!swiper?.initialized) {
              swiper.init();

              // To restore previously active slide
              swiper.setTranslate(this.swiperData.transform);
              swiper.slideTo(this.swiperData.activeIndex, 0, false);
            }
          }

          this.setProgress(progress);
        },
      });
    },
    animationStop() {
      let { animation } = this;

      if (animation && typeof animation === 'function') {
        animation();
      }

      animation = undefined;

      this.swiperDestroy();
    },
    setAnimationEnd() {
      this.animationEnd = window.innerHeight * 0.25;
    },
    handleResize() {
      this.animationStop();
      this.setAnimationEnd();
      this.animationStart();

      this.setHeight();
      this.setPull();
    },
    handleClick(e) {
      const { progress } = this;
      const index = parseInt(e.target.closest('.swiper-slide').dataset.index) - 1;
      const scrollToY = animationData.start + this.animationEnd + 1;

      this.setInitialSlide(index);

      if (progress < 1) {
        scrollTo(scrollToY, { duration: animationData.speed, easing: eases.linear });
      } else if (progress === 1) {
        this.swiper.slideTo(index);
      }
    },
    setHeight(set = true) {
      const { pricingTariffsWrapperEl, pricingTariffsEl } = this;

      if (set) {
        pricingTariffsWrapperEl.style.height = `${pricingTariffsEl.offsetHeight + this.animationEnd}px`;
      } else {
        pricingTariffsWrapperEl.style.height = '';
      }
    },
    setPull(set = true) {
      const { pricingTariffsWrapperEl } = this;
      const windowHeight = window.innerHeight;
      const slideEl = this.$refs.swiper.querySelector('.swiper-slide');
      const slideHeight = slideEl.offsetHeight;
      const value = Math.trunc(
        slideEl.getBoundingClientRect().top +
          window.scrollY -
          72 / 2 -
          (windowHeight - slideHeight) / 2 -
          this.pricingTariffsEl.offsetTop
      );

      if (set) {
        pricingTariffsWrapperEl.style.setProperty('--pull', `${-Math.abs(value)}px`);
      } else {
        pricingTariffsWrapperEl.style.removeProperty('--pull');
      }
    },
    setProgress(progress, set = true) {
      const { pricingTariffsWrapperEl } = this;

      if (set) {
        pricingTariffsWrapperEl.style.setProperty('--progress', progress);
      } else {
        pricingTariffsWrapperEl.style.removeProperty('--progress');
      }
    },
    setInitialSlide(index) {
      const { swiperData, swiperSizes } = this;
      const { inner } = this.$refs;
      const size = -Math.abs(swiperSizes.slideWidth + swiperSizes.spaceBetween);

      swiperData.activeIndex = index;
      swiperData.translate = size * index;
      inner.style.setProperty('--translate', `${size * index}px`);
    },
  },
};
</script>
